import type { ReactNode, TableHTMLAttributes } from 'react';
import styles from './Table.module.css';

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  children: ReactNode;
  /** Tints the row under the cursor. Only worth it when rows are clickable or
      wide enough that the eye loses the line; on a three-column table it is
      noise. */
  hoverable?: boolean;
  /** Names the table for screen readers, because a table announced as "table"
      tells a user nothing about whether it is the one they wanted. Give one
      whenever the screen knows what the table is. It is optional only for a
      table whose content arrives from elsewhere, like one the model writes into
      an answer: nothing names that table, and a generic label such as "Table"
      would satisfy the rule while telling the listener nothing. Its column
      headers are then the only description it has. */
  label?: string;
}

/**
 * A data table.
 *
 * Deliberately thin. Write native `<thead>`, `<tbody>`, `<tr>`, `<th>` and
 * `<td>` inside it and the stylesheet handles the rest, so you keep content-
 * driven column widths and correct header semantics rather than reimplementing
 * them behind a `columns` prop.
 *
 * Two class names are exported for cells that need them: `Table.numeric` for a
 * column of figures, and `Table.actions` for a trailing column of row controls.
 *
 * This is for tens of rows. Past a few hundred, or as soon as you need sorting,
 * virtualisation or column resizing, that is a data-grid problem and belongs to
 * a library built for it rather than to this.
 */
export default function Table({
  children,
  hoverable = false,
  label,
  className,
  ...rest
}: TableProps) {
  return (
    <div className={styles.scroll}>
      <table
        className={[styles.table, hoverable ? styles.hoverable : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
        aria-label={label}
        {...rest}
      >
        {children}
      </table>
    </div>
  );
}

/** Right-aligned, tabular figures. Put it on both the `<th>` and its `<td>`s. */
Table.numeric = styles.numeric;
/** A trailing column of row actions: shrink to content, aligned right. */
Table.actions = styles.actions;

export interface TableEmptyProps {
  children: ReactNode;
  /** Must match the table's column count, or the message will not span the row. */
  colSpan: number;
}

/**
 * The empty state, as a full-width row.
 *
 * Say what would be here and how to put something here. "No members yet" is a
 * fact; "No members yet. Invite someone to get started." is an empty state.
 */
export function TableEmpty({ children, colSpan }: TableEmptyProps) {
  return (
    <tr>
      <td className={styles.empty} colSpan={colSpan}>
        {children}
      </td>
    </tr>
  );
}
